import { useState } from "react";
import { Tractor } from "lucide-react";
import { Carte, COL, Indisponible, Vide, dateFr, isErreur, nombre } from "./shared";

export const RUBRIQUES = {
  phyto: "Phyto", fertilisation: "Fertilisation", biostimulant: "Biostimulants", irrigation: "Irrigation", chantier: "Chantiers",
};
const STATUTS = { brouillon: "Brouillon", valide: "Validé", verrouille: "Verrouillé", corrige: "Corrigé", exporte: "Exporté" };
const ELEMENTS = { N: "N", P2O5: "P₂O₅", K2O: "K₂O" };

const dose = (r) => (r.dose != null ? `${nombre(r.dose)} ${r.unite || ""}`.trim() : "");

function Statut({ s }) {
  if (!s) return null;
  const brouillon = s === "brouillon";
  return (
    <span className="text-[11px] px-2 py-0.5 rounded-full font-medium"
      style={{ background: brouillon ? COL.amberSoft : COL.greenSoft, color: brouillon ? COL.amber : COL.green }}>
      {STATUTS[s] || s}
    </span>
  );
}

function Journal({ lignes, desktop }) {
  if (!lignes.length) return <Vide>Aucune intervention sur la période.</Vide>;
  if (desktop) {
    return (
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-gray-500 border-b" style={{ borderColor: COL.line }}>
              {["Date", "Îlot", "Rubrique", "Intervention", "Produit", "Dose", "Surface", "Opérateur", "Statut"].map((h) => (
                <th key={h} className="py-2 pr-3 font-medium">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {lignes.map((r) => (
              <tr key={`${r.rubrique}-${r.source_id}`} className="border-b last:border-b-0" style={{ borderColor: COL.line }}>
                <td className="py-2 pr-3 whitespace-nowrap">{dateFr(r.date)}</td>
                <td className="py-2 pr-3">{r.ilot_nom || "—"}</td>
                <td className="py-2 pr-3">{RUBRIQUES[r.rubrique]}</td>
                <td className="py-2 pr-3">{r.intervention}</td>
                <td className="py-2 pr-3">{r.produit || "—"}</td>
                <td className="py-2 pr-3 whitespace-nowrap">{dose(r) || "—"}</td>
                <td className="py-2 pr-3 whitespace-nowrap">{r.surface_ha != null ? `${nombre(r.surface_ha)} ha` : "—"}</td>
                <td className="py-2 pr-3">{r.operateur || "—"}</td>
                <td className="py-2 pr-3"><Statut s={r.statut} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }
  return (
    <ul className="divide-y" style={{ borderColor: COL.line }}>
      {lignes.map((r) => (
        <li key={`${r.rubrique}-${r.source_id}`} className="py-2">
          <div className="flex items-center justify-between gap-2">
            <span className="text-sm font-semibold" style={{ color: COL.text }}>{r.produit || r.intervention}</span>
            <span className="text-xs text-gray-500 shrink-0">{dateFr(r.date)}</span>
          </div>
          <div className="flex items-center justify-between gap-2 mt-0.5">
            <span className="text-xs text-gray-500">
              {[RUBRIQUES[r.rubrique], r.ilot_nom, dose(r), r.operateur].filter(Boolean).join(" · ")}
            </span>
            <Statut s={r.statut} />
          </div>
        </li>
      ))}
    </ul>
  );
}

export default function InterventionsCard({ data, journal, periode, desktop, className = "" }) {
  const [tout, setTout] = useState(false);
  const erreur = isErreur(data);
  const lignes = Array.isArray(journal) ? journal : [];
  const visibles = tout ? lignes : lignes.slice(0, desktop ? 4 : 5);
  return (
    <Carte titre="Interventions" numero="5" icon={Tractor} className={className} testId="fiche-interventions"
      action={periode && <span className="text-xs text-gray-500">du {dateFr(periode.debut)} au {dateFr(periode.fin)}</span>}>
      {erreur ? <Indisponible /> : (
        <div className={desktop ? "grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)] gap-6" : ""}>
          <div>
            {data.resume.length ? (
              <ul className="divide-y" style={{ borderColor: COL.line }}>
                {data.resume.map((r) => (
                  <li key={r.rubrique} className="flex items-start justify-between gap-3 py-2">
                    <span className="text-sm" style={{ color: COL.muted }}>{RUBRIQUES[r.rubrique]}</span>
                    <span className="text-right min-w-0">
                      <span className="text-sm font-semibold block">{r.nombre}</span>
                      <span className="text-[11px] text-gray-500 block">{r.resume}</span>
                    </span>
                  </li>
                ))}
              </ul>
            ) : <Vide>Aucune intervention saisie sur la période.</Vide>}
          </div>
          {data.bilan_npk.length > 0 && (
            <div className={desktop ? "" : "pt-3"}>
              <div className="text-sm font-semibold mb-1" style={{ color: COL.text }}>Bilan N-P-K (kg/ha)</div>
              {data.bilan_npk.map((b) => (
                <div key={b.fiche_id} className="mb-2">
                  <div className="text-xs text-gray-500 mb-1">{b.ilot_nom}</div>
                  {b.elements.map((e) => {
                    const pct = e.objectif ? Math.min(100, Math.round((100 * e.realise) / e.objectif)) : null;
                    return (
                      <div key={e.code} className="flex items-center gap-2 py-0.5 text-sm">
                        <span className="w-10 shrink-0">{ELEMENTS[e.code]}</span>
                        <div className="flex-1 h-2 rounded-full" style={{ background: "#EEECE8" }}>
                          {pct != null && <div className="h-2 rounded-full" style={{ width: `${pct}%`, background: COL.green }} />}
                        </div>
                        <span className="w-28 text-right text-xs shrink-0">
                          {nombre(e.realise, 0)}{e.objectif != null ? ` / ${nombre(e.objectif, 0)}` : " (pas d'objectif)"}
                        </span>
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      <div className="pt-3">
        <div className="text-sm font-semibold mb-1" style={{ color: COL.text }}>
          {tout ? "Journal des interventions" : "Dernières interventions"}
        </div>
        {isErreur(journal) ? <Indisponible /> : <Journal lignes={visibles} desktop={desktop} />}
        {lignes.length > visibles.length || tout ? (
          <button type="button" onClick={() => setTout(!tout)} className="min-h-[44px] text-sm font-semibold"
            style={{ color: COL.green }} data-testid="fiche-journal-tout">
            {tout ? "Afficher moins" : `Voir tout le journal (${lignes.length})`}
          </button>
        ) : null}
      </div>
    </Carte>
  );
}
